import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import { BottomNav } from '../../components/feature/BottomNav';
import { japanPrefectures } from '../../mocks/japanPrefectures';

export default function LoginPage() {
  const navigate = useNavigate();
  const [isLogin, setIsLogin] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  // プロフィールのprefectureに基づいて遷移先を決定
  const redirectToPrefecture = (prefName: string) => {
    const matched = japanPrefectures.find((p) => p.name === prefName);
    if (matched && matched.available) {
      navigate(`/prefecture/${matched.id}`);
    } else {
      navigate('/');
    }
  };

  // ログイン済みの場合はプロフィールチェックしてリダイレクト
  useEffect(() => {
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      if (session) {
        const { data } = await supabase
          .from('profiles')
          .select('prefecture')
          .eq('id', session.user.id)
          .maybeSingle();

        if (data?.prefecture) {
          redirectToPrefecture(data.prefecture);
        } else if (data) {
          navigate('/');
        } else {
          navigate('/profile-setup');
        }
      }
    });
  }, [navigate]);

  const showToast = (message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!email || !password) {
      showToast('メールアドレスとパスワードを入力してください', 'error');
      return;
    }
    if (!isLogin && password !== confirmPassword) {
      showToast('パスワードが一致しません', 'error');
      return;
    }

    setLoading(true);

    try {
      if (isLogin) {
        const { data: authData, error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });

        if (error) throw error;

        showToast('ログインしました', 'success');

        if (authData.user) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('prefecture')
            .eq('id', authData.user.id)
            .maybeSingle();

          setTimeout(() => {
            if (profile?.prefecture) {
              redirectToPrefecture(profile.prefecture);
            } else if (profile) {
              navigate('/');
            } else {
              navigate('/profile-setup');
            }
          }, 1000);
        }
      } else {
        // 新規登録
        const { error } = await supabase.auth.signUp({
          email,
          password,
        });

        if (error) throw error;

        showToast('アカウントを作成しました。確認メールをご確認ください。', 'success');
        setTimeout(() => {
          setIsLogin(true);
          setPassword('');
          setConfirmPassword('');
        }, 2000);
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      showToast(
        error.message === 'Invalid login credentials'
          ? 'メールアドレスまたはパスワードが正しくありません'
          : error.message || '認証エラーが発生しました',
        'error'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = async () => {
    setLoading(true);
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/login`,
        },
      });

      if (error) throw error;
    } catch (error: any) {
      console.error('Google auth error:', error);
      showToast(error.message || 'Google認証エラーが発生しました', 'error');
      setLoading(false);
    }
  };

  return (
    <div className="bg-black min-h-screen flex items-center justify-center px-4 pb-24 relative overflow-hidden">
      {/* 呼吸するオーブ背景 */}
      <motion.div
        className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-neon-green/10 rounded-full blur-[120px] pointer-events-none"
        animate={{
          scale: [1, 1.15, 1],
          x: [0, 30, 0],
          y: [0, -20, 0],
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          ease: 'easeInOut',
        }}
      />
      <motion.div
        className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-neon-blue/8 rounded-full blur-[100px] pointer-events-none"
        animate={{
          scale: [1, 1.2, 1],
          x: [0, -40, 0],
          y: [0, 30, 0],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          ease: 'easeInOut',
          delay: 1,
        }}
      />

      {/* Toast */}
      <AnimatePresence>
        {toast && (
          <motion.div
            initial={{ opacity: 0, y: -20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -20, scale: 0.9 }}
            transition={{ type: 'spring', stiffness: 300, damping: 25 }}
            className={`fixed top-6 left-1/2 -translate-x-1/2 z-50 px-golden-md py-golden-sm rounded-golden-lg text-sm font-medium flex items-center gap-golden-xs shadow-2xl ${
              toast.type === 'success'
                ? 'bg-neon-green/20 border border-neon-green/40 text-neon-green'
                : 'bg-red-500/20 border border-red-500/40 text-red-400'
            }`}
            style={{
              boxShadow:
                toast.type === 'success'
                  ? '0 0 30px rgba(0,255,65,0.3)'
                  : '0 0 30px rgba(239,68,68,0.3)',
            }}
          >
            <i className={`text-base ${toast.type === 'success' ? 'ri-checkbox-circle-fill' : 'ri-error-warning-fill'}`} />
            {toast.message}
          </motion.div>
        )}
      </AnimatePresence>

      <div className="w-full max-w-md relative z-10">
        {/* Back link */}
        <Link
          to="/"
          className="inline-flex items-center gap-golden-xs text-gray-500 text-sm hover:text-neon-green transition-colors duration-300 mb-golden-md cursor-pointer"
        >
          <i className="ri-arrow-left-line text-base" />
          ホームに戻る
        </Link>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          className="bg-black/40 backdrop-blur-xl rounded-golden-lg border border-white/[0.08] overflow-hidden relative"
          style={{ boxShadow: '0 0 80px rgba(0, 255, 65, 0.1)' }}
        >
          {/* Decorative top glow */}
          <motion.div
            className="absolute top-0 left-1/2 -translate-x-1/2 w-48 h-1 bg-gradient-to-r from-transparent via-neon-green to-transparent rounded-full"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Header */}
          <div className="pt-golden-lg pb-golden-base px-golden-md text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className="w-16 h-16 flex items-center justify-center rounded-full bg-neon-green/10 border border-neon-green/30 mx-auto mb-golden-sm"
              style={{ boxShadow: '0 0 20px rgba(0,255,65,0.2)' }}
            >
              <i className="ri-user-line text-2xl text-neon-green" />
            </motion.div>
            <motion.h1
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.3 }}
              className="font-display text-golden-xl text-white tracking-wider mb-golden-xxs"
            >
              {isLogin ? 'LOGIN' : 'SIGN UP'}
            </motion.h1>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
              className="text-gray-500 text-xs tracking-wider"
            >
              {isLogin ? 'アカウントにログイン' : '新しいアカウントを作成'}
            </motion.p>
          </div>

          {/* Body */}
          <div className="px-golden-md pb-golden-lg">
            {/* Google Login */}
            <motion.button
              type="button"
              onClick={handleGoogleLogin}
              disabled={loading}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="whitespace-nowrap w-full flex items-center justify-center gap-golden-sm px-golden-base py-golden-sm rounded-golden-md bg-white/[0.03] border border-white/10 text-white text-sm font-medium hover:bg-white/[0.06] hover:border-white/20 transition-all duration-300 cursor-pointer mb-golden-sm disabled:opacity-50 disabled:cursor-wait"
              style={{
                boxShadow: '0 0 0 rgba(255,255,255,0.1)',
                transition: 'all 0.3s ease',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 0 20px rgba(255,255,255,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 0 0 rgba(255,255,255,0.1)';
              }}
            >
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
              </svg>
              {isLogin ? 'Googleアカウントでログイン' : 'Googleアカウントで作成'}
            </motion.button>

            {/* Divider */}
            <div className="flex items-center gap-golden-sm mb-golden-sm">
              <div className="flex-1 h-px bg-white/10" />
              <span className="text-gray-600 text-[10px] tracking-widest">OR</span>
              <div className="flex-1 h-px bg-white/10" />
            </div>

            {/* Form */}
            <AnimatePresence mode="wait">
              <motion.form
                key={isLogin ? 'login' : 'signup'}
                initial={{ opacity: 0, x: isLogin ? -20 : 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: isLogin ? 20 : -20 }}
                transition={{ duration: 0.3 }}
                onSubmit={handleSubmit}
                className="space-y-golden-sm"
              >
                <div>
                  <label className="block text-gray-400 text-xs mb-golden-xxs tracking-wider">メールアドレス</label>
                  <div className="relative">
                    <i className="ri-mail-line absolute left-golden-sm top-1/2 -translate-y-1/2 text-gray-600 text-sm" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full pl-golden-lg pr-golden-base py-golden-sm rounded-golden-md bg-white/[0.03] border border-white/10 text-white text-sm focus:border-neon-green focus:outline-none transition-all duration-300 placeholder:text-gray-700"
                      placeholder="email@example.com"
                      style={{
                        boxShadow: '0 0 0 rgba(0,255,65,0)',
                        transition: 'all 0.3s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(0,255,65,0.2)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.boxShadow = '0 0 0 rgba(0,255,65,0)';
                      }}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-gray-400 text-xs mb-golden-xxs tracking-wider">パスワード</label>
                  <div className="relative">
                    <i className="ri-lock-line absolute left-golden-sm top-1/2 -translate-y-1/2 text-gray-600 text-sm" />
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full pl-golden-lg pr-11 py-golden-sm rounded-golden-md bg-white/[0.03] border border-white/10 text-white text-sm focus:border-neon-green focus:outline-none transition-all duration-300 placeholder:text-gray-700"
                      placeholder="••••••••"
                      style={{
                        boxShadow: '0 0 0 rgba(0,255,65,0)',
                        transition: 'all 0.3s ease',
                      }}
                      onFocus={(e) => {
                        e.currentTarget.style.boxShadow = '0 0 15px rgba(0,255,65,0.2)';
                      }}
                      onBlur={(e) => {
                        e.currentTarget.style.boxShadow = '0 0 0 rgba(0,255,65,0)';
                      }}
                    />
                    <motion.button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      className="whitespace-nowrap absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-gray-600 hover:text-gray-400 transition-colors cursor-pointer"
                    >
                      <i className={`text-sm ${showPassword ? 'ri-eye-off-line' : 'ri-eye-line'}`} />
                    </motion.button>
                  </div>
                </div>

                {!isLogin && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <label className="block text-gray-400 text-xs mb-golden-xxs tracking-wider">パスワード確認</label>
                    <div className="relative">
                      <i className="ri-lock-check-line absolute left-golden-sm top-1/2 -translate-y-1/2 text-gray-600 text-sm" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="w-full pl-golden-lg pr-golden-base py-golden-sm rounded-golden-md bg-white/[0.03] border border-white/10 text-white text-sm focus:border-neon-green focus:outline-none transition-all duration-300 placeholder:text-gray-700"
                        placeholder="••••••••"
                        style={{
                          boxShadow: '0 0 0 rgba(0,255,65,0)',
                          transition: 'all 0.3s ease',
                        }}
                        onFocus={(e) => {
                          e.currentTarget.style.boxShadow = '0 0 15px rgba(0,255,65,0.2)';
                        }}
                        onBlur={(e) => {
                          e.currentTarget.style.boxShadow = '0 0 0 rgba(0,255,65,0)';
                        }}
                      />
                    </div>
                  </motion.div>
                )}

                {isLogin && (
                  <div className="text-right">
                    <button type="button" className="whitespace-nowrap text-gray-500 text-xs hover:text-neon-green transition-colors cursor-pointer">
                      パスワードを忘れた方
                    </button>
                  </div>
                )}

                <motion.button
                  type="submit"
                  disabled={loading}
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="whitespace-nowrap w-full py-golden-sm rounded-golden-md bg-neon-green text-black font-bold text-sm tracking-wider transition-all duration-300 cursor-pointer disabled:opacity-50 disabled:cursor-wait flex items-center justify-center gap-golden-xs"
                  style={{
                    boxShadow: '0 0 20px rgba(0,255,65,0.3)',
                    transition: 'all 0.3s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!loading) {
                      e.currentTarget.style.boxShadow = '0 0 30px rgba(0,255,65,0.5)';
                    }
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.boxShadow = '0 0 20px rgba(0,255,65,0.3)';
                  }}
                >
                  {loading ? (
                    <>
                      <i className="ri-loader-4-line text-base animate-spin" />
                      処理中...
                    </>
                  ) : isLogin ? (
                    'ログイン'
                  ) : (
                    'アカウント作成'
                  )}
                </motion.button>
              </motion.form>
            </AnimatePresence>

            {/* Toggle */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-golden-sm text-center"
            >
              <span className="text-gray-500 text-xs">
                {isLogin ? 'アカウントをお持ちでない方' : '既にアカウントをお持ちの方'}
              </span>
              <button
                type="button"
                onClick={() => {
                  setIsLogin(!isLogin);
                  setPassword('');
                  setConfirmPassword('');
                }}
                className="whitespace-nowrap ml-2 text-neon-green text-xs font-medium hover:underline cursor-pointer"
              >
                {isLogin ? '新規登録' : 'ログイン'}
              </button>
            </motion.div>
          </div>
        </motion.div>
      </div>

      {/* Bottom Navigation */}
      <BottomNav />
    </div>
  );
}
